import * as React from "react";
import { useState, useRef } from "react";

import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import { mobileBreakpoint } from "../../../../entrypoints/theme";
import Breadcrumbs from "../Product/Breadcrumbs";
import { ProductTile } from "@project-stories/ecommerce/Product/ProductTile";
import ProductTileSkeleton from "@project-stories/ecommerce/Product/ProductTileSkeleton";
import FiltersSidebar from "@src/stories/ecommerce/Collection/FiltersSidebar";
import CollectionToolbar from "@project-stories/ecommerce/Collection/CollectionToolbar";

export const SearchResults = ({ settings }) => {
  const [query, setQuery] = useState([]);
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState([]);
  const isMobile = useMobileBreakpoint(mobileBreakpoint);
  const [isSidebarVisible, setSidebarVisible] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const paginationHaveLoaded = useRef(false);

  const toggleSidebar = () => {
    setSidebarVisible((prevState) => {
      const newState = !prevState;
      document.body.classList.toggle("no-scroll", newState);
      return newState;
    });
  };

  const getCurrentPage = () => {
    const params = new URLSearchParams(window.location.search);
    return Number(params.get("page")) || 1;
  };

  const {
    columns_mobile,
    columns_desktop,
    column_gap_mobile,
    column_gap_desktop,
    row_gap_mobile,
    row_gap_desktop,
    id,
    show_breadcrumbs,
    default_filter_view,
  } = settings.section;

  React.useEffect(() => {  
    // Initialize search with current URL parameters
    const initializeSearch = () => {
      // Check if Alpine.js is already handling the initial search
      if ((window as any).alpineHandlingInitialSearch) return;
      
      const urlParams = new URLSearchParams(window.location.search);
      const queryFromUrl = urlParams.get('q');
      const filtersFromUrl = urlParams.get('filters');
      const sortFromUrl = urlParams.get('sort_by');
      
      if (queryFromUrl) {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('searchrequest', {
            detail: {
              query: queryFromUrl,
              type: 'search',
              filters: filtersFromUrl || '',
              index: sortFromUrl || (window as any).defaultSort || 'relevance',
              source: 'react-initial-search',
              productsPerPage: settings.products_per_page * getCurrentPage() 
            }
          }));
        }, 300);
      }
    };

    // Small delay to let Alpine.js initialize first
    setTimeout(initializeSearch, 100);
    const handleFiltersResults = (event) => {
      if (event.detail.type === "search") {
        if (event.detail.filters) {
          setFilters(event.detail.filters);
        }
      }
    };

    const handleProductsResults = (event) => {
      if (event.detail.type === "search") {
        setQuery(event.detail.query);
        setProducts(event.detail.products);
        setHasLoaded(true);
      }

      if (paginationHaveLoaded.current) {
        window.dispatchEvent(
          new CustomEvent("current-products-updated", {
            detail: {
              totalViewed: event.detail.products.length,
            },
          }),
        );
      } else {
        const intervalId = setInterval(() => {
          if (paginationHaveLoaded.current) {
            window.dispatchEvent(
              new CustomEvent("current-products-updated", {
                detail: {
                  totalViewed: event.detail.products.length,
                },
              }),
            );

            clearInterval(intervalId);
          }
        }, 100);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        setSidebarVisible(false);
      }
    };

    const handlePaginationLoad = () => {
      paginationHaveLoaded.current = true;
    }

    const handlePopState = () => {
      // Check if Alpine.js is already handling this popstate event
      const alpineHandling = (window as any).alpineHandlingPopstate;
      if (alpineHandling) return;
      
      // Set flag to prevent Alpine.js from also handling this
      (window as any).reactHandlingPopstate = true;
      setTimeout(() => {
        (window as any).reactHandlingPopstate = false;
      }, 1000);
      
      // Trigger a re-fetch when browser navigation occurs (back/forward buttons)
      const currentUrl = new URL(window.location.href);
      const hasFilters = currentUrl.searchParams.has('filters');
      const hasQuery = currentUrl.searchParams.has('q');
      
      // Dispatch URL change event with current parameters
      window.dispatchEvent(new CustomEvent("url-changed", {
        detail: {
          url: window.location.href,
          pathname: window.location.pathname,
          search: window.location.search,
          hasFilters,
          hasQuery
        }
      }));
    };

    const handleUrlChange = () => {
      // Check if Alpine.js is handling the popstate event
      if ((window as any).alpineHandlingPopstate) return;

      // Add small delay to prevent race conditions with popstate events
      setTimeout(() => {
        // Parse current URL parameters
        const newUrl = new URL(window.location.href);
        const params = newUrl.searchParams;
        
        // Get default sort option
        const defaultSort = (window as any).defaultSort || 'default';
        
        // Only dispatch if we have a query parameter
        const queryParam = params.get('q');
        if (queryParam) {
          // Dispatch searchrequest event to trigger search reload
          window.dispatchEvent(new CustomEvent('searchrequest', {
            detail: {
              query: queryParam,
              type: 'search',
              filters: params.get('filters') || '',
              index: params.get('sort_by') || defaultSort,
              source: 'react-url-change'
            }
          }));
        }
      }, 200);
    };
    
    // Initialize search with current URL parameters
    initializeSearch();
    
    // Delay the collection-search-loaded event to ensure Alpine.js components are ready
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("collection-search-loaded"));
    }, 400);

    window.addEventListener("pagination-have-loaded", handlePaginationLoad);
    window.addEventListener("product-list", handleProductsResults);
    window.addEventListener("filter-list", handleFiltersResults);
    window.addEventListener("keydown", handleEscapeKey);
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("url-changed", handleUrlChange);

    // Cleanup the event listeners on component unmount
    return () => {
      window.removeEventListener("product-list", handleProductsResults);
      window.removeEventListener("filter-list", handleFiltersResults);
      window.removeEventListener("keydown", handleEscapeKey);
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("url-changed", handleUrlChange);
      window.removeEventListener("pagination-have-loaded", handlePaginationLoad);
    };
  }, []);

  return (
    <>
      <FiltersSidebar
        isVisible={isSidebarVisible}
        filters={filters}
        metaobject_filters={settings.filters}
        toggleSidebar={toggleSidebar}
        mode={default_filter_view}
      />
      <div className="search-results__toolbar flex justify-between mb-4 items-center">
        {!isMobile && show_breadcrumbs && (
          <Breadcrumbs
            templateName={settings.template.name}
            shop={settings.shop}
            collection={null}
            product={null}
            routes={settings.routes}
            blog={null}
            article={null}
            page={settings.page}
            request={settings.request}
            pageTitle={query}
          />
        )}
        <CollectionToolbar toggleSidebar={toggleSidebar} mode={default_filter_view} />
      </div>
      <div
        id="product-grid"
        className={`search product-grid grid grid-cols-${columns_mobile} lg:grid-cols-${columns_desktop} gap-x-[${column_gap_mobile}px] lg:gap-x-[${column_gap_desktop}px] gap-y-[${row_gap_mobile}px] lg:gap-y-[${row_gap_desktop}px] auto-cols-max`}
        data-id={id}
      >
        {hasLoaded === false && (
          Array.from({ length: settings.products_per_page * getCurrentPage() || 12 }).map((_, i) => (
            <ProductTileSkeleton key={`skeleton-${i}`} />
          ))
        )}
        {hasLoaded && (
          <>
            {products.map((product, index) => (
              <div key={`product-${index}`} className={"search-tile"}>
                <ProductTile product={product} siblings={undefined} />
              </div>
            ))}
          </>
        )}
      </div>
    </>
  );
};

export default SearchResults;
